import React from "react";
import { Modal, Table, Button, Spinner } from "react-bootstrap";
import { FiDownload } from "react-icons/fi";
import type { ReportColumnsSpec } from "@core/components/ReportTreeTable";
import {
  formatBooleanValue,
  getBooleanValue,
  getNumericValue,
  getViewableValueText,
} from "@core/reports/utils/reportCellValues";

/**
 * Reusable eyeball-preview modal for a single entity's report submission (D7).
 * Children-based shell: callers pass the body content —
 *  - Pastor variant: <MinistersReportView data={...} showHeader={false} />
 *  - Default (generic) variant: <GenericSubmissionPreview ... /> (below).
 */

type ReportPreviewModalProps = {
  show: boolean;
  onHide: () => void;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  children?: React.ReactNode;
  /** When provided (approved reports), shows a Download PDF button in the footer. */
  onDownload?: () => void;
  downloading?: boolean;
};

const ReportPreviewModal: React.FC<ReportPreviewModalProps> = ({
  show,
  onHide,
  title,
  subtitle,
  children,
  onDownload,
  downloading = false,
}) => (
  <Modal show={show} onHide={onHide} size="lg" centered scrollable>
    <Modal.Header closeButton>
      <Modal.Title className="h6 mb-0">
        {title}
        {subtitle ? <div className="text-muted small fw-normal">{subtitle}</div> : null}
      </Modal.Title>
    </Modal.Header>
    <Modal.Body>{children}</Modal.Body>
    {onDownload ? (
      <Modal.Footer>
        <Button
          variant="primary"
          className="btn-action"
          onClick={onDownload}
          disabled={downloading}
          aria-busy={downloading}
          title="Download this report as a PDF"
        >
          {downloading ? (
            <><Spinner animation="border" size="sm" className="me-2" aria-hidden />Generating…</>
          ) : (
            <><FiDownload className="me-2" aria-hidden />Download PDF</>
          )}
        </Button>
      </Modal.Footer>
    ) : null}
  </Modal>
);

export default ReportPreviewModal;

/**
 * Default generic content: a template's KPIs grouped by activity, reconstructed
 * from the already-loaded tree row + that table's columnsSpec (no extra fetch).
 * Shows every KPI in the spec (blanks as "—") so the modal is the full template.
 */
export function formatPreviewCell(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";

  const booleanValue = getBooleanValue(value);
  if (booleanValue !== null) return formatBooleanValue(booleanValue);

  // Structured KPI values (SELECT/GRID/grid_rollup) before numeric — objects are never scalars.
  const viewable = getViewableValueText(value);
  if (viewable) return viewable;

  const numericValue = getNumericValue(value);
  if (numericValue !== null) return numericValue.toLocaleString();

  return typeof value === "string" ? value : String(value);
}

export const GenericSubmissionPreview: React.FC<{
  columnsSpec: ReportColumnsSpec;
  row: Record<string, unknown>;
}> = ({ columnsSpec, row }) => {
  const groups = columnsSpec.groups.filter((g) => g.columns.length > 0);

  if (groups.length === 0) {
    return <p className="text-muted mb-0">No data captured for this submission.</p>;
  }
  return (
    <>
      {groups.map((group, gi) => (
        <div className="mb-3" key={`${group.header}-${gi}`}>
          {group.header ? <div className="fw-semibold mb-1">{group.header}</div> : null}
          <Table responsive size="sm" className="mb-0 align-middle ntcog-table">
            <tbody>
              {group.columns.map((leaf) => (
                <tr key={leaf.id}>
                  <td>{leaf.header}</td>
                  <td className="text-end fw-semibold">{formatPreviewCell(row[leaf.field])}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      ))}
    </>
  );
};
